import { useMutation } from '@apollo/client';
import { User } from 'mission-control-common-components';
import { useCallback } from 'react';

import type { TraffickingFilters } from '../../../../../apis/graphql/types';
import { CREATE_TRAFFICKING_CUSTOM_FILTER } from '../mutations/createTraffickingCustomFilter';
import { normalizeFilterPayloadKeys } from '../normalizeFilterPayloadKeys';
import type {
  CreateTraffickingCustomFilterMutationResponse,
  CreateTraffickingCustomFilterMutationVariables,
  CustomFilterPayload,
  CustomFilterVisibility,
  TraffickingCustomFilterFull,
} from '../types';

type UseCreateCustomFilterResult = {
  createFilter: (
    name: string,
    payload: CustomFilterPayload,
    visibility: CustomFilterVisibility
  ) => Promise<TraffickingCustomFilterFull>;
  loading: boolean;
  error: Error | undefined;
};

export function useCreateCustomFilter(): UseCreateCustomFilterResult {
  const [mutate, { loading, error }] = useMutation<
    CreateTraffickingCustomFilterMutationResponse,
    CreateTraffickingCustomFilterMutationVariables
  >(CREATE_TRAFFICKING_CUSTOM_FILTER, {
    refetchQueries: ['GET_TRAFFICKING_CUSTOM_FILTERS'],
  });

  const createFilter = useCallback(
    async (
      name: string,
      payload: CustomFilterPayload,
      visibility: CustomFilterVisibility
    ): Promise<TraffickingCustomFilterFull> => {
      const result = await mutate({
        variables: {
          input: {
            filterName: name,
            pageScope: 'TRAFFICKING',
            visibility,
            filterPayload: payload,
          },
        },
      });
      if (!result.data) {
        const gqlMessage = result.errors?.[0]?.message ?? '';
        const isDuplicate =
          gqlMessage.toLowerCase().includes('duplicate') || gqlMessage.toLowerCase().includes('already exists');
        throw new Error(isDuplicate ? 'A filter with this name already exists' : 'Failed to create custom filter');
      }
      const raw = result.data.createTraffickingCustomFilter;
      const currentUser = User.getUserInfo().userInfo?.email ?? '';
      return {
        id: raw.id,
        name: raw.filterName,
        visibility: raw.visibility as TraffickingCustomFilterFull['visibility'],
        type: raw.filterType as TraffickingCustomFilterFull['type'],
        createdByMe: (raw.createdBy ?? '').toLowerCase() === currentUser.toLowerCase(),
        payload: normalizeFilterPayloadKeys((raw.filterPayload as unknown) as TraffickingFilters),
      };
    },
    [mutate]
  );

  return { createFilter, loading, error };
}
